import React, {
  useEffect, useMemo, useRef, useState,
} from "react";

import { SearchOutlined } from "@ant-design/icons";
import { Button, Checkbox, Input } from "antd";
import moment from "moment";
import { toast } from "react-toastify";

import useAbortController from "../../../../Common/hooks/useAbortController";
import Loading from "../../../../Common/Loading";
import NoResultsGIF from "../../../../Common/NoResultsGIF";
import StyledScrollbar from "../../../../Common/UIComponents/StyledScrollbar";
import StyledTooltip from "../../../../Common/UIComponents/StyledTooltip";
import CreateStepLayout from "./CreateStepLayout";

import { convertUtcToLocal } from "../../../../Common/utils/dateUtils";
import { getSubmissionId } from "../utils";

import { getSubmittedSubmissionsInfo } from "../../../../../actions/sampleSubmission";

import pumpkinGIF from "../../../../../assets/images/environment/pumpkin.gif";

import "./SelectSampleSubmissions.css";

const FILTER_OPTIONS = [
  { value: "all", label: "All Submissions" },
  { value: "recent", label: "Recently Submitted" },
  { value: "this_week", label: "This Week" },
];

const formatSubmittedOn = (submitDate) => {
  if (!submitDate) return "-";
  return moment(new Date(convertUtcToLocal(submitDate))).format("DD MMM YYYY | hh:mm a");
};

const formatSampleCount = (count) => {
  const n = Number(count);
  if (!Number.isFinite(n) || n < 0) return "-";
  return String(n).padStart(2, "0");
};

const getTotalTests = (submission) => {
  const value = submission?.total_tests ?? submission?.num_tests ?? submission?.tests_count;
  if (value == null || value === "") return "-";
  return String(value);
};

const isPickupScheduled = (row) => row?.pickup_scheduled === true;
const isDeletedSubmission = (row) => row?.status === -3 || row?.status === "-3";
const isRowDisabled = (row) => isPickupScheduled(row) || isDeletedSubmission(row);

const getDisabledTooltip = (row) => {
  if (isDeletedSubmission(row)) return "This submission has been deleted";
  if (isPickupScheduled(row)) return "Already used in a pickup request";
  return "";
};

/** Convert local YYYY-MM-DD range to UTC ISO strings (same as SubmissionsSearch). */
const convertLocalDateRangetoUTC = (startDate, endDate) => {
  if (startDate || endDate) {
    const localStart = moment(startDate).startOf("day");
    const utcStart = moment(localStart).utc().format();
    const localEnd = endDate
      ? moment(endDate).endOf("day")
      : moment(startDate).endOf("day");
    const utcEnd = moment(localEnd).utc().format();
    return [utcStart, utcEnd];
  }
  return ["", ""];
};

const getDateRangeForFilter = (filter) => {
  if (filter === "recent") {
    const dateFromLocal = moment().format("YYYY-MM-DD");
    const dateToLocal = dateFromLocal;
    const [dateFromUTC, dateToUTC] = convertLocalDateRangetoUTC(dateFromLocal, dateToLocal);
    return {
      date_from: dateFromUTC,
      date_to: dateToUTC,
      user_selected_date_from: dateFromLocal,
      user_selected_date_to: dateToLocal,
    };
  }
  if (filter === "this_week") {
    const dateFromLocal = moment().startOf("week").format("YYYY-MM-DD");
    const dateToLocal = moment().format("YYYY-MM-DD");
    const [dateFromUTC, dateToUTC] = convertLocalDateRangetoUTC(dateFromLocal, dateToLocal);
    return {
      date_from: dateFromUTC,
      date_to: dateToUTC,
      user_selected_date_from: dateFromLocal,
      user_selected_date_to: dateToLocal,
    };
  }
  return {
    date_from: "",
    date_to: "",
    user_selected_date_from: "",
    user_selected_date_to: "",
  };
};

export default function SelectSampleSubmissions({
  pickupTypeLabel = "FedEx Pickup",
  pickupTypeKey,
  initialSelectedSubmissions = [],
  onBack,
  onContinue,
}) {
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingNewContent, setLoadingNewContent] = useState(false);
  const [paginationInfo, setPaginationInfo] = useState({
    nextPageLink: "",
    currentPage: 1,
    totalPages: 1,
  });
  const [selectedById, setSelectedById] = useState(() => {
    const map = new Map();
    (initialSelectedSubmissions || []).forEach((row) => {
      const id = getSubmissionId(row);
      if (id && !isRowDisabled(row)) map.set(id, row);
    });
    return map;
  });
  const tableScrollbarRef = useRef(null);
  const { setNewAbortController, clearAbortController } = useAbortController();

  const fetchSubmissions = async ({
    page = 1,
    searchValue = appliedSearch,
    filterValue = filter,
  } = {}) => {
    const pageNum = Number(page) || 1;
    if (pageNum > 1) {
      setLoadingNewContent(true);
    } else {
      setLoading(true);
      if (tableScrollbarRef.current) {
        tableScrollbarRef.current.scrollToTop();
      }
    }

    const abortController = setNewAbortController();
    const {
      date_from,
      date_to,
      user_selected_date_from,
      user_selected_date_to,
    } = getDateRangeForFilter(filterValue);

    // Match Sample Submission entry call (Submissions.js mount → GET /samplesubmission/)
    const payload = {
      submission_id: "",
      page: pageNum,
      search_value: searchValue ?? "",
      search_by: "all",
      status: "",
      date_from: date_from ?? "",
      date_to: date_to ?? "",
      user_selected_date_from: user_selected_date_from ?? "",
      user_selected_date_to: user_selected_date_to ?? "",
      submitted_by: "",
      sample_category: "",
    };

    const {
      success,
      message,
      result,
      next_page_link,
      total_page,
    } = await getSubmittedSubmissionsInfo(payload, abortController.signal);

    if (abortController.signal.aborted) {
      if (pageNum > 1) setLoadingNewContent(false);
      return;
    }

    if (success) {
      const nextSubmissions = Array.isArray(result) ? result : [];
      setSubmissions((prev) => (
        pageNum > 1 ? [...prev, ...nextSubmissions] : nextSubmissions
      ));
      setPaginationInfo({
        nextPageLink: next_page_link || "",
        currentPage: pageNum,
        totalPages: Math.max(1, Number(total_page) || 1),
      });
      const disabledIds = new Set(
        nextSubmissions
          .filter(isRowDisabled)
          .map(getSubmissionId)
          .filter(Boolean),
      );
      if (disabledIds.size > 0) {
        setSelectedById((prev) => {
          let changed = false;
          const next = new Map(prev);
          disabledIds.forEach((id) => {
            if (next.has(id)) {
              next.delete(id);
              changed = true;
            }
          });
          return changed ? next : prev;
        });
      }
    } else {
      toast.error(message || "Failed to load submissions");
      if (pageNum > 1) {
        // Prevent infinitely re-firing the scroll API call
        if (tableScrollbarRef.current) {
          tableScrollbarRef.current.scrollTop -= 100;
        }
      } else {
        setSubmissions([]);
        setPaginationInfo({ nextPageLink: "", currentPage: 1, totalPages: 1 });
      }
    }

    clearAbortController();

    if (pageNum > 1) {
      /* Fix safari bug where api is called repeatedly.
        Scroll slightly from bottom so it's outside of the tolerance range. */
      if (tableScrollbarRef.current) {
        tableScrollbarRef.current.scrollTop -= 4;
      }
      setLoadingNewContent(false);
    } else {
      setLoading(false);
      if (tableScrollbarRef.current) {
        tableScrollbarRef.current.scrollToTop();
      }
    }
  };

  useEffect(() => {
    fetchSubmissions({ page: 1, searchValue: "", filterValue: "all" });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (loadingNewContent && tableScrollbarRef.current) {
      tableScrollbarRef.current.scrollToBottom();
    }
  }, [loadingNewContent]);

  const selectedCount = selectedById.size;

  const handleSearch = (value) => {
    const next = value ?? search;
    setSearch(next);
    setAppliedSearch(next);
    fetchSubmissions({ page: 1, searchValue: next });
  };

  const handleFilterChange = (value) => {
    setFilter(value);
    fetchSubmissions({ page: 1, filterValue: value });
  };

  const handleScrollToBottom = (e) => {
    const {
      nextPageLink, currentPage, totalPages,
    } = paginationInfo;
    const isLastPage = parseInt(currentPage, 10) === parseInt(totalPages, 10);

    if (isLastPage || loadingNewContent || loading) return;

    const tolerance = 3;
    const bottom = Math.abs(
      Math.ceil(e.contentScrollHeight - e.scrollTop) - Math.ceil(e.clientHeight),
    ) <= tolerance;

    if (!bottom) return;

    const nextPage = nextPageLink
      ? nextPageLink.split("page=")[1].split("&")[0]
      : String(parseInt(currentPage, 10) + 1);
    fetchSubmissions({ page: nextPage });
  };

  const toggleRow = (id, row) => {
    if (!id || isRowDisabled(row)) return;
    setSelectedById((prev) => {
      const next = new Map(prev);
      if (next.has(id)) next.delete(id);
      else next.set(id, row);
      return next;
    });
  };

  const selectedSamplesTotal = useMemo(() => (
    Array.from(selectedById.values()).reduce((sum, row) => {
      const n = Number(row?.num_samples);
      return sum + (Number.isFinite(n) ? n : 0);
    }, 0)
  ), [selectedById]);

  const handleContinue = () => {
    if (selectedCount === 0) return;
    onContinue(Array.from(selectedById.values()).filter((row) => !isRowDisabled(row)));
  };

  const selectionLabel = selectedCount === 0
    ? "No submissions selected"
    : `${selectedCount} submissions selected · ${selectedSamplesTotal} samples total`;

  const footer = (
    <div className="SelectSampleSubmissions__Footer">
      <span
        className={`SelectSampleSubmissions__SelectionCount${selectedCount > 0 ? " SelectSampleSubmissions__SelectionCount--active" : ""}`}
      >
        {selectionLabel}
      </span>
      <Button
        type="primary"
        className="SelectSampleSubmissions__ContinueBtn"
        disabled={selectedCount === 0}
        onClick={handleContinue}
      >
        Continue
      </Button>
    </div>
  );

  return (
    <CreateStepLayout
      pickupTypeLabel={pickupTypeLabel}
      pickupTypeKey={pickupTypeKey}
      activeStepKey="select-submissions"
      onBack={onBack}
      footer={footer}
    >
      <div className="SelectSampleSubmissions__Body">
        <div className="SelectSampleSubmissions__Header">
          <h1 className="SelectSampleSubmissions__Title">Select Sample Submissions</h1>
          <p className="SelectSampleSubmissions__Subtitle">
            Select one or more submissions with Submitted status to include in this pickup
          </p>
        </div>

        <div className="SelectSampleSubmissions__Toolbar">
          <Input
            allowClear
            value={search}
            onChange={(e) => {
              const value = e.target.value;
              setSearch(value);
              if (!value) handleSearch("");
            }}
            onPressEnter={() => handleSearch(search)}
            placeholder="Search sample submissions"
            className="SelectSampleSubmissions__Search"
            prefix={<SearchOutlined className="SelectSampleSubmissions__SearchIcon" />}
          />
          <div className="SelectSampleSubmissions__Filters">
            {FILTER_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                className={`SelectSampleSubmissions__FilterChip${filter === option.value ? " SelectSampleSubmissions__FilterChip--active" : ""}`}
                onClick={() => handleFilterChange(option.value)}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>

        <div className="SelectSampleSubmissions__TablePanel">
          <StyledScrollbar
            ref={tableScrollbarRef}
            className="SelectSampleSubmissions__TableScroll"
            disableTrackYWidthCompensation
            disableTracksWidthCompensation
            onScrollStop={handleScrollToBottom}
          >
            <div className="SelectSampleSubmissions__TableWrap">
              <table className="SelectSampleSubmissions__Table">
                <thead>
                  <tr>
                    <th className="SelectSampleSubmissions__CheckCol" aria-hidden />
                    <th>Submission Name</th>
                    <th>Submitted On</th>
                    <th>Submitted By</th>
                    <th className="SelectSampleSubmissions__NumCol">Samples</th>
                    <th className="SelectSampleSubmissions__NumCol">Total Tests</th>
                  </tr>
                </thead>
                <tbody>
                  {!loading && submissions.map((row) => {
                    const id = getSubmissionId(row);
                    const disabled = isRowDisabled(row);
                    const disabledTooltip = disabled ? getDisabledTooltip(row) : "";
                    const checked = !disabled && selectedById.has(id);
                    const rowClassNames = [
                      checked ? "SelectSampleSubmissions__Row--selected" : "",
                      disabled ? "SelectSampleSubmissions__Row--disabled" : "",
                    ].filter(Boolean).join(" ");

                    const tableRow = (
                      <tr
                        className={rowClassNames || undefined}
                        onClick={() => toggleRow(id, row)}
                        aria-disabled={disabled || undefined}
                      >
                        <td
                          className="SelectSampleSubmissions__CheckCol"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Checkbox
                            checked={checked}
                            disabled={disabled}
                            onChange={() => toggleRow(id, row)}
                          />
                        </td>
                        <td>
                          <div className="SelectSampleSubmissions__Name">{row.submit_name || "-"}</div>
                          <div className="SelectSampleSubmissions__Po">{row.po || "-"}</div>
                        </td>
                        <td>{formatSubmittedOn(row.submit_date)}</td>
                        <td>{row.submitted_by || "-"}</td>
                        <td className="SelectSampleSubmissions__Samples SelectSampleSubmissions__NumCol">
                          {formatSampleCount(row.num_samples)}
                        </td>
                        <td className="SelectSampleSubmissions__NumCol">{getTotalTests(row)}</td>
                      </tr>
                    );

                    const rowContent = disabledTooltip ? (
                      <StyledTooltip
                        title={disabledTooltip}
                        placement="top"
                        getPopupContainer={() => document.body}
                      >
                        {tableRow}
                      </StyledTooltip>
                    ) : tableRow;

                    return (
                      <React.Fragment key={id || row.submit_name}>
                        {rowContent}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
              {loading && <div className="SelectSampleSubmissions__Loading"><Loading /></div>}
              {!loading && submissions.length === 0 && (
                <NoResultsGIF image={pumpkinGIF} message="No submissions found" />
              )}
            </div>
            {!loading && loadingNewContent && submissions.length > 0 && (
              <div className="SelectSampleSubmissions__Loading"><Loading /></div>
            )}
          </StyledScrollbar>
        </div>
      </div>
    </CreateStepLayout>
  );
}
